import { Tabs } from '~/components/ui/Tabs';
import { useTranslation } from 'react-i18next';
import { OrderList } from '~/pages/orders/sections/OrderList.tsx';
import { useLocation } from 'react-router';
import { useGetOrders, useGetOrdersCountByStatus } from '~/api';
import { useSelector } from 'react-redux';
import type { RootState } from '~/redux/store';
import type { OrderStatus, OrderStatusCount } from '~/graphql/graphql';

type TabDetail = {
  labelKey: string;
  link: string;
  count: number;
  statusKey?: OrderStatus | undefined;
  noOrdersMessageKey: string;
};
type OrderStatusTabDetails = {
  [key: string]: TabDetail;
};

export default function Orders() {
  const currentOrderTab = useLocation().pathname.split('/')[2] || '';
  const { data: ordersCounts } = useGetOrdersCountByStatus();
  const role = useSelector((state: RootState) => state.auth.user?.role);
  const statusCounts = ordersCounts?.ordersCount as OrderStatusCount;

  const allTabDetails = getOrderStatusTabDetails(
    statusCounts,
    role?.toLowerCase() || ''
  );
  const tabDetails = allTabDetails[currentOrderTab];
  const statusKey = tabDetails?.statusKey;

  const { data } = useGetOrders(statusKey);
  const orders = data?.orders || [];

  const { t } = useTranslation('order');

  const orderTabs = Object.values(allTabDetails).map(
    (tabDetail: TabDetail) => ({
      label: t(tabDetail.labelKey),
      count: tabDetail.count,
      link: tabDetail.link,
      component: (
        <OrderList
          noOrderTitle={t('order.no_orders_found')}
          orders={orders}
          noOrderMessage={t(tabDetail.noOrdersMessageKey)}
        />
      ),
    })
  );
  const handleTabChange = () => {};

  return (
    <div className="w-full flex flex-col gap-8">
      <title>{t('order.title')}</title>
      <div className="rounded-lg ">
        <div className="flex items-center gap-2 overflow-x-auto">
          <Tabs
            title={t('order.title')}
            tabs={orderTabs}
            onTabChange={handleTabChange}
            basePath="orders"
          />
        </div>
      </div>
    </div>
  );
}

function sumAllOrderCounts(statusCount: OrderStatusCount) {
  return (
    (statusCount?.draft || 0) +
    (statusCount?.pendingSellerConfirmation || 0) +
    (statusCount?.confirmed || 0) +
    (statusCount?.shipped || 0) +
    (statusCount?.delivered || 0)
  );
}

const getOrderStatusTabDetails = (
  statusCount: OrderStatusCount,
  role: string
): OrderStatusTabDetails => {
  const allTabs: OrderStatusTabDetails = {
    all: {
      labelKey: 'order.all_orders',
      statusKey: undefined,
      link: 'all',
      count: sumAllOrderCounts(statusCount),
      noOrdersMessageKey: `order.${role}.no_orders_message`,
    },
    'pending-seller-confirmation': {
      labelKey:
        role === 'seller' ? 'order.to_confirm' : 'order.awaiting_confirmation',
      statusKey: 'PENDING_SELLER_CONFIRMATION',
      link: 'pending-seller-confirmation',
      count: statusCount?.pendingSellerConfirmation || 0,
      noOrdersMessageKey: `order.${role}.no_awaiting_confirmation_orders_message`,
    },
    confirmed: {
      labelKey: role === 'seller' ? 'order.to_fulfill' : 'order.to_receive',
      statusKey: 'CONFIRMED',
      link: 'confirmed',
      count: statusCount?.confirmed || 0,
      noOrdersMessageKey: `order.${role}.no_confirmed_orders_message`,
    },
    shipped: {
      labelKey: 'order.shipped_orders',
      statusKey: 'SHIPPED',
      link: 'shipped',
      count: statusCount?.shipped || 0,
      noOrdersMessageKey: 'order.no_shipped_orders_message',
    },
    delivered: {
      labelKey: 'order.delivered_orders',
      statusKey: 'DELIVERED',
      link: 'delivered',
      count: statusCount?.delivered || 0,
      noOrdersMessageKey: 'order.no_delivered_orders_message',
    },
  };

  return allTabs;
};
